import { getBaseUrl } from 'lib/baseUrl'
import type { Suggestion } from 'lib/suggestions'
import {
  type BaseEditor,
  Editor,
  Element,
  Node,
  Path,
  Range,
  Text,
  Transforms,
} from 'slate'
import { getEmployeeAlias, getStoryDataFromUrl, suggestLinks } from './actions'
export type LinkTarget = {
  url: string
  text: string
}

export type LinksEditor = {
  linksPluginInstalled: true
  suggestLinksFetcher: (search: string) => Promise<Suggestion[]>
  parseTarget: (target: string) => Promise<LinkTarget>
}

export function formatTargetUrl(target: string): string {
  if (isEmail(target)) {
    return `mailto:${target}`
  }

  if (target.startsWith('http')) {
    return target
  }

  return `https://${target}`
}

export const withLinks = Object.assign(
  (editor: BaseEditor) => {
    return withLinks.bind({
      suggestLinksFetcher: async () => [],
      parseTarget: async (target: string) => ({
        url: formatTargetUrl(target),
        text: target,
      }),
    })(editor)
  },
  {
    bind({
      suggestLinksFetcher,
      parseTarget,
    }: Omit<LinksEditor, 'linksPluginInstalled'>) {
      return <T extends BaseEditor>(editor: T) => {
        const e = editor as T & Editor & LinksEditor
        const { isInline, insertText, insertData, normalizeNode } = e

        e.linksPluginInstalled = true
        e.suggestLinksFetcher = suggestLinksFetcher
        e.parseTarget = parseTarget

        e.isInline = (element) => {
          return element.type === 'link' ? true : isInline(element)
        }

        e.insertText = (text) => {
          // escaping first also keeps a url that is inserted at the end of a
          // link from replacing that link's target instead of adding a new one
          escapeLinkAtEnd(e)

          if (text && isUrl(text)) {
            parseTarget(text).then(insertLink.bind(null, e))
          } else {
            insertText(text)
          }
        }

        e.insertData = (data) => {
          const text = data.getData('text/plain')

          escapeLinkAtEnd(e)

          if (text && isUrl(text)) {
            parseTarget(text).then(insertLink.bind(null, e))
          } else {
            insertData(data)
          }
        }

        e.normalizeNode = (entry) => {
          const [node, path] = entry
          if (Element.isElement(node) && node.type === 'link') {
            const content = Editor.string(e, path)
            if (!content) {
              Transforms.removeNodes(e, { at: path })
              return
            }
          }
          normalizeNode(entry)
        }

        return e
      }
    },
  },
)

export const storiesAndPostsFetcher = suggestLinks

export async function storiesAndPostsParseTarget(
  target: string,
): Promise<LinkTarget> {
  const checkInternalUrl = target.match(
    /^(wiki|posts|stories|projects|offers|stories\/collections|people\/employees)\/([\w-]+)/,
  )

  let url = ' '

  if (isEmail(target)) {
    url = `mailto:${target}`
  } else if (checkInternalUrl) {
    if (isEmployee(target)) {
      try {
        const id = checkInternalUrl[2] ?? ''
        const employee = await getEmployeeAlias(id)

        url = `https://tbf.digital/user/${employee?.alias}`
      } catch (e) {
        console.error(e)
      }
    } else {
      url = `${getBaseUrl()}/${target}`
    }
  } else if (target.startsWith('http')) {
    url = target
  } else {
    url = `https://${target}`
  }

  const story = await getStoryDataFromUrl(url)
  const text = story ? story.title : url

  return {
    url,
    text,
  }
}

/**
 * Links are inline elements, so a collapsed selection at their end edge is still
 * "inside" the link and everything typed there would extend it. Move the
 * selection out of the link so that writing on continues outside of it.
 */
export function escapeLinkAtEnd(editor: Editor) {
  const { selection } = editor
  if (!selection || !Range.isCollapsed(selection)) return

  const [match] = Editor.nodes(editor, {
    at: selection,
    match: (n) => Element.isElement(n) && n.type === 'link',
    mode: 'lowest',
  })
  if (!match) return

  const [, path] = match
  if (!Editor.isEnd(editor, selection.anchor, path)) return

  // slate's normalisation puts a text node behind every inline element, but it
  // might not be there yet while normalisation is suspended. only a direct
  // sibling will do - anything further ahead belongs to another link or block.
  const next = Path.next(path)
  if (!Node.has(editor, next) || !Text.isText(Node.get(editor, next))) {
    Transforms.insertNodes(editor, { text: '' }, { at: next })
  }
  Transforms.select(editor, { path: next, offset: 0 })
}

export async function insertLink(editor: Editor, { url, text }: LinkTarget) {
  if (!editor.selection) return

  const [match] = Editor.nodes(editor, {
    match: (n) => Element.isElement(n) && n.type === 'link',
  })

  if (match) {
    const [, path] = match
    Transforms.setNodes(editor, { url }, { at: path })
  } else {
    const isCollapsed = Range.isCollapsed(editor.selection)
    if (isCollapsed) {
      // the trailing text node keeps the cursor outside of the new link
      Transforms.insertNodes(editor, [
        {
          type: 'link',
          url,
          children: [{ text }],
        },
        { text: '' },
      ])
    } else {
      Transforms.wrapNodes(
        editor,
        {
          type: 'link',
          url,
          children: [],
        },
        { split: true },
      )
      Transforms.collapse(editor, { edge: 'end' })
      escapeLinkAtEnd(editor)
    }
  }
}

export function removeLink(editor: Editor) {
  Transforms.unwrapNodes(editor, {
    match: (n) => Element.isElement(n) && n.type === 'link',
  })
}

export function isUrl(s: any): boolean {
  const pattern =
    /^(?:https?:\/\/)?(?:[\da-z.-]+)\.(?:[a-z.]{2,63})(?:\/[\w.-]*)*(?:[\/?#][^#\s]*)?$/i
  return pattern.test(s)
}

export function isEmail(s: any): boolean {
  const pattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i
  return pattern.test(s)
}

export function isEmployee(s: any): boolean {
  const pattern = /^people\/employees(?:\/|$)/
  return pattern.test(s)
}

export function isLink(editor: Editor) {
  return !!getActiveLinkUrl(editor)
}

export function isPhone(value: string) {
  return /^(tel:)?\+?[0-9][0-9\- \/]+$/.test(value)
}

export function getActiveLinkUrl(editor: Editor) {
  let [match] = Editor.nodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) && Element.isElement(n) && n.type === 'link',
  })

  if (!match) {
    const { selection } = editor
    if (selection && Range.isCollapsed(selection)) {
      const point = Editor.after(editor, selection.focus.path)
      const [nextMatch] = Editor.nodes(editor, {
        at: point,
        match: (n) =>
          !Editor.isEditor(n) && Element.isElement(n) && n.type === 'link',
      })
      match = nextMatch
    }
  }
  if (match) {
    const link = match[0] as Element & { type: 'link' }
    return link.url
  }
  return null
}
